import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowRightIcon, CheckCircleIcon, ChevronRightIcon, SearchIcon, AlertCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { sectorPages, getSectorPageBySlug } from '../data/sectorPages';
import { getClientCaseBySlug } from '../data/allClientCases';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
export const SectorPage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const page = getSectorPageBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!page) {
    return <Navigate to="/websites" replace data-id="element-2032" data-mp="2xydvp0cp1t9z" />;
  }
  const relatedCase = page.caseSlug ? getClientCaseBySlug(page.caseSlug) : undefined;
  const anderePaginas = sectorPages.filter(p => p.slug !== page.slug).slice(0, 6);
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faq.map(f => ({
      '@type': 'Question',
      name: f.vraag,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.antwoord
      }
    }))
  };
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2033" data-mp="2y78hy45m49pf">
      <SEO title={page.metaTitle} description={page.metaDescription} canonical={`https://noorddigital.nl/website-voor/${page.slug}`} jsonLd={[buildServiceSchema({
      name: page.h1,
      description: page.metaDescription,
      path: `/website-voor/${page.slug}`,
      serviceType: `Webdesign voor ${page.sector}`
    }), buildBreadcrumbList([{
      name: 'Websites',
      path: '/websites'
    }, {
      name: page.sector,
      path: `/website-voor/${page.slug}`
    }]), faqSchema]} data-id="element-2034" data-mp="2ahnylk5jrafm" />
      <Header data-id="element-2035" data-mp="3qw60879tq6i6" />

      {/* Hero */}
      <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20" data-id="element-2036" data-mp="3qv4dv6ncxxl5">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2037" data-mp="1sy8qq17x0w3">
          <nav aria-label="Kruimelpad" className="mb-8" data-id="element-2038" data-mp="6sjizwlu8vo4">
            <ol className="flex items-center gap-1.5 text-sm text-white text-opacity-70 flex-wrap" data-id="element-2039" data-mp="6v5rpzkgth7x">
              <li data-id="element-2040" data-mp="bq2vos2w10dy">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-2041" data-mp="gr8pkt8aatp2">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2042" data-mp="3f9xj560y45o9">
                <ChevronRightIcon className="h-3.5 w-3.5 text-white text-opacity-40" data-id="element-2043" data-mp="3f6rwdxfshfvp" />
              </li>
              <li data-id="element-2044" data-mp="3k5mm7jc6c4pb">
                <Link to="/websites" className="hover:text-white transition-colors" data-id="element-2045" data-mp="3p7byf2b4uoez">
                  Websites
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2046" data-mp="3tzbx6rfgvb9z">
                <ChevronRightIcon className="h-3.5 w-3.5 text-white text-opacity-40" data-id="element-2047" data-mp="2wiektchv9fsk" />
              </li>
              <li className="font-medium text-white" aria-current="page" data-id="element-2048" data-mp="31h7yb4x5l6sq">
                {page.sector}
              </li>
            </ol>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2049" data-mp="1ilk7mjupzuyy">
            <div className="lg:col-span-7" data-id="element-2050" data-mp="1em30llznapro">
              <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl" data-id="element-2051" data-mp="1eixd2b6nzvy2">
                {page.h1}
              </h1>
              <p className="text-white text-opacity-90 text-lg leading-relaxed mb-8" data-id="element-2052" data-mp="14ndf7j8jin6o">
                {page.intro}
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-2053" data-mp="zp2a1lt6x0us">
                <Link to="/prijsscan" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg" data-id="element-2054" data-mp="uw910lqov6cw">
                  Start de website scan
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-2055" data-mp="lwck56ajjbse" />
                </Link>
                <a href="/#contact" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-2056" data-mp="lu9ljxvboac2">
                  Plan een kennismaking
                </a>
              </div>
            </div>

            {/* Herkenbaar */}
            <div className="lg:col-span-5" data-id="element-2057" data-mp="2c293k4x89gkt">
              <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-2058" data-mp="2gzsf2x2jjf92">
                <div className="flex items-center gap-2 mb-5" data-id="element-2059" data-mp="2h13j8xzwep0p">
                  <AlertCircleIcon className="h-5 w-5 text-[#007AFF]" data-id="element-2060" data-mp="229tkf4a72lyc" />
                  <p className="text-sm font-semibold text-[#111827]" data-id="element-2061" data-mp="278o0hygs1wer">
                    Herken je dit?
                  </p>
                </div>
                <ul className="space-y-3" data-id="element-2062" data-mp="1tbrf4cjfh759">
                  {page.herkenbaar.map(item => <li key={item} className="flex items-start gap-3" data-id="element-2063" data-mp="1y9j7f7nziv9z">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#007AFF] flex-shrink-0" data-id="element-2064" data-mp="1jca9tw838v9t" />
                      <span className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2065" data-mp="1o9s90uwazw4m">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat er op de site hoort */}
      <section className="w-full bg-white py-20 md:py-24" data-id="element-2066" data-mp="170ho19cms7hj">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2067" data-mp="16ywot6fdg0dc">
          <div className="max-w-3xl mb-14" data-id="element-2068" data-mp="123zhq51nk133">
            <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-2069" data-mp="1grkoc8xqkifr">
              Wat er op de site van een {page.sector.toLowerCase()} thuishoort
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2070" data-mp="1prqvze6r6o70">
              Niet elk onderdeel is voor elk bedrijf even belangrijk. In de
              kennismaking bepalen we samen waar de winst voor jou zit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-2071" data-mp="1zovhrcwoshna">
            {page.onderdelen.map((o, i) => <motion.div key={o.titel} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: Math.min(i, 4) * 0.05
          }} className="bg-[#F7F8FA] rounded-2xl p-8 border border-gray-100 flex flex-col" data-id="element-2072" data-mp="1urdadqybiahe" data-mpi={i}>
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center mb-5" data-id="element-2073" data-mp="25fzwokmlpg2i">
                  <CheckCircleIcon className="h-5 w-5 text-[#007AFF]" data-id="element-2074" data-mp="2eele2cgc6p1p" />
                </div>
                <h3 className="font-bold text-[#111827] text-lg mb-3" data-id="element-2075" data-mp="2je6aaf44gjaq">
                  {o.titel}
                </h3>
                <p className="text-[#6B7280] leading-relaxed" data-id="element-2076" data-mp="2oc7tr15gmkmq">{o.tekst}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Vindbaarheid */}
      <section className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-2077" data-mp="2x5bd1q2sdrpa">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2078" data-mp="324fo6m99tzi9">
          <div className="max-w-3xl" data-id="element-2079" data-mp="322baxdphfo5b">
            <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-6" data-id="element-2080" data-mp="372zbor3ijjcq">
              <SearchIcon className="h-6 w-6 text-[#007AFF]" data-id="element-2081" data-mp="3h8h45tdnpv0d" />
            </div>
            <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-5" data-id="element-2082" data-mp="3c7t98wmb4u0v">
              Hoe klanten in deze branche zoeken
            </h2>
            <p className="text-[#4B5563] text-lg leading-relaxed" data-id="element-2083" data-mp="379jf8pcbjzgu">
              {page.vindbaarheid}
            </p>
          </div>
        </div>
      </section>

      {/* Case als bewijs */}
      {relatedCase && <section className="w-full bg-white py-20 md:py-24" data-id="element-2084" data-mp="3une6fbm53xl">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2085" data-mp="3uzvllvrx1o5b">
            <div className="bg-[#F7F8FA] rounded-2xl overflow-hidden border border-gray-100 grid grid-cols-1 lg:grid-cols-2" data-id="element-2086" data-mp="3q7c7mxpbgqzz">
              <div className="h-64 lg:h-auto" data-id="element-2087" data-mp="3m5qvjeklwgqu">
                <img src={relatedCase.heroImage} alt={`${relatedCase.clientName}, klantcase Noord Digital`} loading="lazy" className="w-full h-full object-cover object-top" data-id="element-2088" data-mp="mj0uteoe7u7l" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-2089" data-mp="11gcliqbena97">
                <p className="text-sm font-semibold text-[#007AFF] mb-3" data-id="element-2090" data-mp="11f1fejuhlqex">
                  Dit deden we voor een {page.sector.toLowerCase()}
                </p>
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-2091" data-mp="rj7xeky014pr">
                  {page.caseNaam}
                </h2>
                <p className="text-[#6B7280] leading-relaxed mb-8" data-id="element-2092" data-mp="wf5dcq5co920">
                  {page.caseTekst}
                </p>
                <Link to={`/cases/${relatedCase.slug}`} className="inline-flex items-center gap-2 text-[#007AFF] font-semibold w-fit" data-id="element-2093" data-mp="1p5x4383nc5hy">
                  Lees de volledige case
                  <ArrowRightIcon className="h-4 w-4" data-id="element-2094" data-mp="1aar00qo42gr9" />
                </Link>
              </div>
            </div>
          </div>
        </section>}

      {/* FAQ */}
      <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-2095" data-mp="3uxu8v8hknv3u">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[820px]" data-id="element-2096" data-mp="3scao28egzpd">
          <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-10" data-id="element-2097" data-mp="3q7x0skp8mbw">
            Veelgestelde vragen
          </h2>
          <div className="space-y-4" data-id="element-2098" data-mp="2d1s4icqolvux">
            {page.faq.map(f => <details key={f.vraag} className="bg-white rounded-xl border border-gray-200 p-6 group" data-id="element-2099" data-mp="2d3cz22czxess">
                <summary className="font-semibold text-[#111827] cursor-pointer list-none flex items-center justify-between gap-4" data-id="element-2100" data-mp="2qyxgsugznqym">
                  {f.vraag}
                  <ChevronRightIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0 group-open:rotate-90 transition-transform" data-id="element-2101" data-mp="1zax2nkelof0u" />
                </summary>
                <p className="text-[#6B7280] leading-relaxed mt-4" data-id="element-2102" data-mp="2825q3zz4a9kj">
                  {f.antwoord}
                </p>
              </details>)}
          </div>
        </div>
      </section>

      {/* Andere sectoren */}
      <section className="w-full bg-white py-20 md:py-24" data-id="element-2103" data-mp="1flmls334bo15">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2104" data-mp="1fjs20s3l21yn">
          <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-8" data-id="element-2105" data-mp="1bjrnz8kebss4">
            Ook voor deze branches
          </h2>
          <div className="flex flex-wrap gap-3" data-id="element-2106" data-mp="lm9yvdgeyj6f">
            {anderePaginas.map(p => <Link key={p.slug} to={`/website-voor/${p.slug}`} className="inline-flex items-center gap-2 bg-[#F7F8FA] hover:bg-[#E8F0FE] border border-gray-200 text-[#111827] font-medium px-5 py-3 rounded-lg transition-colors" data-id="element-2107" data-mp="gq2pfaot6pjq">
                {p.sector}
                <ArrowRightIcon className="h-4 w-4 text-[#007AFF]" data-id="element-2108" data-mp="2qbitv0mgbfr" />
              </Link>)}
          </div>
        </div>
      </section>

      {/* Slot-CTA */}
      <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-16 md:py-20" data-id="element-2109" data-mp="3u2cuuly5yjex">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] text-center" data-id="element-2110" data-mp="3p5mj2ate69zb">
          <h2 className="font-bold text-white text-3xl md:text-4xl mb-4" data-id="element-2111" data-mp="3l5m2okmqfopm">
            Benieuwd wat dit voor jouw bedrijf oplevert?
          </h2>
          <p className="text-white text-opacity-90 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-2112" data-mp="3b51vz2gg1s00">
            In een gesprek van een half uur kijken we naar je huidige
            vindbaarheid en vertellen we wat we zouden aanpakken. Je zit daarna
            tot niets verplicht.
          </p>
          <a href="/#contact" className="inline-flex items-center px-8 py-4 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg" data-id="element-2113" data-mp="20m0763tq7il7">
            Plan een kennismaking
            <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-2114" data-mp="24kzic661wbl3" />
          </a>
        </div>
      </section>

      <Footer data-id="element-2115" data-mp="17uynpqj030vv" />
    </div>;
};